import { Note, NoteDuration, NoteVelocity } from "../model/core";
import { EventToNoteSequenceBuffer } from "./EventToNoteSequenceBuffer";
import { startLoggingMIDIInput, stopLoggingMIDIInput } from "./MidiController";
import { RecordArmController } from "./RecordingController";

export type NoteEventNoteData = {
    note: Note,
    velocity?: NoteVelocity,
}

export type TimedNoteEventData = NoteEventNoteData & {
    duration?: NoteDuration,
    start?: number,
}
export type NoteActivityType = { event: 'noteon' | 'noteoff' };
export type NoteActivityListener = (_: NoteActivityType) => void;
export type NoteCompleteActivityListener = (_: TimedNoteEventData) => void;
export type NoteEvent = NoteEventNoteData & NoteActivityType;
export type NoteEventListener = (_: NoteEvent) => void;

const recordingController = RecordArmController.getInstance();

const midiCaptureFunction = (noteActivityObserver: NoteActivityObserver) => {
    return (event) => {
        if (!event) return;
        if (!event.data) return;
        const _note = event.data[1];
        // FIXME use velocity
        const velocity: NoteVelocity = event.data[2] as NoteVelocity || 127;
        const sourceId = (event.currentTarget)?.id;

        const note = Note.fromIndex(_note);
        // note out of range
        if (!note) return;

        if (event.data[0] === 0x90) {
            noteActivityObserver.update(sourceId, {
                note: note,
                event: 'noteon',
                velocity: velocity,
            });
        } else if (event.data[0] === 0x80) {
            noteActivityObserver.update(sourceId, {
                note: note,
                event: 'noteoff',
            });
        }
    }
}

export class NoteActivityObserver {
    private static instance: NoteActivityObserver;
    private trackNoteListeners: Map<number, Map<number, (NoteEventListener)>> = new Map();
    private trackEventListeners: Map<number, NoteActivityListener> = new Map();
    private trackNoteCompleteListeners: Map<number, (NoteCompleteActivityListener)> = new Map();
    private sourceTrackAssingments: Map<string, number[]> = new Map(); // sourceId -> trackIds
    private noteBuffer: EventToNoteSequenceBuffer;

    private constructor() {
        this.noteBuffer = new EventToNoteSequenceBuffer((sourceId, start, note, duration, velocity) => {
            this.sourceTrackAssingments.get(sourceId)
                ?.map(trackId => this.trackNoteCompleteListeners.get(trackId))
                .flat()
                .forEach((listener) => {
                    if (!listener) return;
                    listener({
                        start,
                        note: Note.fromIndex(note),
                        duration: new NoteDuration(0 | duration),
                        velocity: velocity as NoteVelocity
                    });
                });
        });
    }
    static getInstance() {
        if (!NoteActivityObserver.instance) {
            NoteActivityObserver.instance = new NoteActivityObserver();
        }
        return NoteActivityObserver.instance;
    }

    /**
     * Update triggered by a midi source. Finds the tracks assinged to this source and notifies their listeners.
     * @param sourceId
     * @param n
     * @param on
     */
    update(
        sourceId: string,
        data: NoteEvent
    ) {
        this.sourceTrackAssingments.get(sourceId)?.forEach(trackId => {
            this.trackNoteListeners.get(trackId)?.get(data.note.index)?.(data);
            this.trackEventListeners.get(trackId)?.(data);
        });

        const tick = recordingController.getCurrentRecordingTick();
        if (tick == null) return;
        if (data.event === 'noteon') {
            this.noteBuffer.processEvent({
                sourceId,
                note: data.note.index,
                type: 'start',
                timestamp: tick,
                velocity: data.velocity || 127
            });
        } else {
            this.noteBuffer.processEvent({
                sourceId,
                note: data.note.index,
                type: 'end',
                timestamp: tick,
                velocity: 0
            });
        }
    }

    clearListeners() {
        this.trackNoteListeners.clear();
        this.trackEventListeners.clear();
        this.trackNoteCompleteListeners.clear();
    }

    drop(trackId: number) {
        this.trackEventListeners.delete(trackId);
    }

    dropNoteListener(
        trackId: number,
        n: number,
    ) {
        const trackListener = this.trackNoteListeners.get(trackId);
        if (!trackListener) return;

        const trackNoteListener = trackListener.get(n);
        if (trackNoteListener) {
            trackListener.delete(n);
        }
        if (trackListener.size === 0) {
            this.trackNoteListeners.delete(trackId);
        }
    }

    dropNoteCompleteListener(trackId: number) {
        this.trackNoteCompleteListeners.delete(trackId);
    }

    listen(
        trackId: number,
        l: NoteEventListener
    ) {
        if (!this.trackEventListeners.has(trackId)) {
            this.trackEventListeners.set(trackId, l);
        }
    }

    listenForNoteSpecificEvent(
        trackId: number,
        n: number,
        l: NoteActivityListener
    ) {
        if (!this.trackNoteListeners.has(trackId)) {
            this.trackNoteListeners.set(trackId, new Map());
        }
        const trackListeners = this.trackNoteListeners.get(trackId)!;
        trackListeners.set(n, l);
    }

    listenForCompleteNote(
        trackId: number,
        listener: NoteCompleteActivityListener
    ) {
        if (!this.trackNoteCompleteListeners.has(trackId)) {
            this.trackNoteCompleteListeners.set(trackId, listener);
        }
    }

    assignSourceToTrack(
        { midiAccess, sourceId }: { midiAccess: any, sourceId: string },
        trackId: number
    ) {
        if (!this.sourceTrackAssingments.has(sourceId)) {
            // if we haven't seen this source before, start logging it
            this.sourceTrackAssingments.set(sourceId, []);
            startLoggingMIDIInput(
                midiAccess,
                { inputId: sourceId },
                midiCaptureFunction(this)
            );
        }
        const assignedTracks = this.sourceTrackAssingments.get(sourceId)!;
        if (!assignedTracks.includes(trackId)) {
            assignedTracks.push(trackId);
        }
    }

    unassignSourceFromTrack(
        { midiAccess, sourceId }: { midiAccess: any, sourceId: string },
        trackId: number
    ) {
        if (!this.sourceTrackAssingments.has(sourceId)) return;
        const assignedTracks = this.sourceTrackAssingments.get(sourceId)!.filter(t => t !== trackId);
        this.sourceTrackAssingments.set(sourceId, assignedTracks);
        if (assignedTracks.length === 0) {
            this.sourceTrackAssingments.delete(sourceId);
            stopLoggingMIDIInput(
                midiAccess,
                { inputId: sourceId }
            );
        }
    }
}
